'use client'

import { cn } from '@neo/lib/utils'
import { Badge } from '@neo/primitives/badge'
import { Button } from '@neo/primitives/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@neo/primitives/context-menu'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@neo/primitives/hover-card'
import { Input } from '@neo/primitives/input'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
} from '@radix-ui/react-icons'
// LazyMotion + `m` (not `motion`) so we don't ship framer-motion's full ~30kb feature bundle — only
// the small `domAnimation` set these rows need (chevron rotate + collapse/selection-header tweens).
import { AnimatePresence, domAnimation, LazyMotion, m } from 'framer-motion'
import { Box, Folder, Info, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface TreeViewItem {
  id: string
  name: string
  type: string
  children?: TreeViewItem[]
  checked?: boolean
  disableCheckbox?: boolean
  defaultChecked?: boolean
  // Lazy picker: a collection whose children are being fetched on expand (shows a spinner row).
  isLoading?: boolean
}

export interface TreeViewIconMap {
  [key: string]: React.ReactNode | undefined
}

export interface TreeViewMenuItem {
  id: string
  label: string
  icon?: React.ReactNode
  action: (items: TreeViewItem[]) => void
}

export interface TreeViewProps {
  className?: string
  data: TreeViewItem[]
  showExpandAll?: boolean
  showCheckboxes?: boolean
  checkboxPosition?: 'left' | 'right'
  searchPlaceholder?: string
  selectionText?: string
  showSearchBox?: boolean
  showItemDetailsOnHover?: boolean
  showSelectionHeader?: boolean
  showSelectionBadges?: boolean
  checkboxLabels?: {
    check: string
    uncheck: string
  }
  getIcon?: (item: TreeViewItem, depth: number) => React.ReactNode
  onSelectionChange?: (selectedItems: TreeViewItem[]) => void
  onAction?: (action: string, items: TreeViewItem[]) => void
  onCheckChange?: (item: TreeViewItem, checked: boolean) => void
  iconMap?: TreeViewIconMap
  menuItems?: TreeViewMenuItem[]
  defaultCheckedIds?: string[]
  onNewSelectionChange?: (newlySelectedItems: TreeViewItem[]) => void
  // Lazy picker: fired when a node is EXPANDED (opened) — the parent fetches that collection's
  // children on demand. Called once per open; the parent dedups/caches.
  onExpand?: (item: TreeViewItem) => void
}

interface TreeItemProps {
  item: TreeViewItem
  depth?: number
  selectedIds: Set<string>
  lastSelectedId: React.MutableRefObject<string | null>
  onSelect: (ids: Set<string>) => void
  expandedIds: Set<string>
  onToggleExpand: (id: string, isOpen: boolean) => void
  getIcon?: (item: TreeViewItem, depth: number) => React.ReactNode
  onAction?: (action: string, items: TreeViewItem[]) => void
  onAccessChange?: (item: TreeViewItem, hasAccess: boolean) => void
  allItems: TreeViewItem[]
  showAccessRights?: boolean
  checkboxPosition?: 'left' | 'right'
  itemMap: Map<string, TreeViewItem>
  iconMap?: TreeViewIconMap
  menuItems?: TreeViewMenuItem[]
  getSelectedItems: () => TreeViewItem[]
  showItemDetailsOnHover?: boolean
  showSelectionBadges?: boolean
}

// Helper function to build a map of all items by ID
const buildItemMap = (items: TreeViewItem[]): Map<string, TreeViewItem> => {
  const map = new Map<string, TreeViewItem>()
  const processItem = (item: TreeViewItem) => {
    map.set(item.id, item)
    item.children?.forEach(processItem)
  }
  items.forEach(processItem)
  return map
}

// Update the getCheckState function to work bottom-up
const getCheckState = (
  item: TreeViewItem,
  itemMap: Map<string, TreeViewItem>,
): 'checked' | 'unchecked' | 'indeterminate' => {
  // Get the original item from the map
  const originalItem = itemMap.get(item.id)
  if (!originalItem) return 'unchecked'

  // If it's a leaf node (no children), return its check state
  // Consider both checked and defaultChecked states
  if (!originalItem.children || originalItem.children.length === 0) {
    return originalItem.checked || originalItem.defaultChecked ? 'checked' : 'unchecked'
  }

  // For parent items, first check if the parent itself is checked or defaultChecked
  if (originalItem.checked || originalItem.defaultChecked) {
    return 'checked'
  }

  // Count the check states of immediate children
  let checkedCount = 0
  let indeterminateCount = 0

  originalItem.children.forEach((child) => {
    const childState = getCheckState(child, itemMap)
    if (childState === 'checked') checkedCount += 1
    if (childState === 'indeterminate') indeterminateCount += 1
  })

  // Calculate parent state based on children states
  const totalChildren = originalItem.children.length

  // If all children are checked
  if (checkedCount === totalChildren) {
    return 'checked'
  }
  // If any child is checked or indeterminate
  if (checkedCount > 0 || indeterminateCount > 0) {
    return 'indeterminate'
  }
  // If no children are checked or indeterminate
  return 'unchecked'
}

// Add this default icon map
const defaultIconMap: TreeViewIconMap = {
  file: <Box className="h-4 w-4 text-status-danger" />,
  folder: <Folder className="h-4 w-4 text-primary/80" />,
}

const TreeItem = ({
  item,
  depth = 0,
  selectedIds,
  lastSelectedId,
  onSelect,
  expandedIds,
  onToggleExpand,
  getIcon,
  onAction,
  onAccessChange,
  allItems,
  showAccessRights,
  checkboxPosition,
  itemMap,
  iconMap = defaultIconMap,
  menuItems,
  getSelectedItems,
  showItemDetailsOnHover = true,
  showSelectionBadges = true,
}: TreeItemProps): JSX.Element => {
  const [isHovered, setIsHovered] = useState(false)
  const isOpen = expandedIds.has(item.id)
  const isSelected = selectedIds.has(item.id)
  const itemRef = useRef<HTMLDivElement>(null)
  const [selectionStyle, setSelectionStyle] = useState('')

  // Get all visible items in order
  const getVisibleItems = useCallback(
    (items: TreeViewItem[]): TreeViewItem[] => {
      let visibleItems: TreeViewItem[] = []

      items.forEach((childItem) => {
        visibleItems.push(childItem)
        if (childItem.children && expandedIds.has(childItem.id)) {
          visibleItems = [...visibleItems, ...getVisibleItems(childItem.children)]
        }
      })

      return visibleItems
    },
    [expandedIds],
  )

  useEffect(() => {
    if (!isSelected) {
      setSelectionStyle('')
      return
    }

    // Get all visible items from the entire tree
    const visibleItems = getVisibleItems(allItems)
    const currentIndex = visibleItems.findIndex((i) => i.id === item.id)

    const prevItem = visibleItems[currentIndex - 1]
    const nextItem = visibleItems[currentIndex + 1]

    const isPrevSelected = prevItem && selectedIds.has(prevItem.id)
    const isNextSelected = nextItem && selectedIds.has(nextItem.id)

    const roundTop = !isPrevSelected
    const roundBottom = !isNextSelected

    setSelectionStyle(`${roundTop ? 'rounded-t-md' : ''} ${roundBottom ? 'rounded-b-md' : ''}`)
  }, [isSelected, selectedIds, expandedIds, item.id, getVisibleItems, allItems])

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    let newSelection = new Set(selectedIds)

    if (!itemRef.current) return

    if (e.shiftKey && lastSelectedId.current !== null) {
      const items = Array.from(document.querySelectorAll('[data-tree-item]')) as HTMLElement[]
      const lastIndex = items.findIndex(
        (el) => el.getAttribute('data-id') === lastSelectedId.current,
      )
      const currentIndex = items.findIndex((el) => el === itemRef.current)
      const [start, end] = [Math.min(lastIndex, currentIndex), Math.max(lastIndex, currentIndex)]

      items.slice(start, end + 1).forEach((el) => {
        const id = el.getAttribute('data-id')
        const parentFolderClosed = el.closest('[data-folder-closed="true"]')
        const isClosedFolder = el.getAttribute('data-folder-closed') === 'true'

        // Skip synthetic, non-selectable rows (the "Showing first N pages…" footer) so a range sweep
        // can't add them to the selection and over-count the "N selected" header.
        if (id && !id.startsWith('__more__') && (isClosedFolder || !parentFolderClosed)) {
          newSelection.add(id)
        }
      })
    } else if (e.ctrlKey || e.metaKey) {
      if (newSelection.has(item.id)) {
        newSelection.delete(item.id)
      } else {
        newSelection.add(item.id)
      }
    } else {
      newSelection = new Set([item.id])
      // Open folder on single click if it's a folder
      if (item.children && isSelected) {
        onToggleExpand(item.id, !isOpen)
      }
    }

    lastSelectedId.current = item.id
    onSelect(newSelection)
  }

  // Helper function removed as it's not being used

  const handleAccessClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onAccessChange) {
      const currentState = getCheckState(item, itemMap)
      // Toggle between checked and unchecked, treating indeterminate as unchecked
      const newChecked = currentState !== 'checked'
      onAccessChange(item, newChecked)
    }
  }

  const renderIcon = () => {
    if (item.type === 'collection') {
      return null // No icon for collections
    }

    // If a custom getIcon function is provided, use it. This allows override.
    if (getIcon) {
      return getIcon(item, depth)
    }

    // Handle specific case for top-level articles (depth 0)
    if (depth === 0 && item.type === 'article') {
      // Use icon from iconMap for 'article', or 'file', or default 'file' icon
      return iconMap.article || iconMap.file || defaultIconMap.file
    }

    // Don't show icons for 'file' type at top level (depth 0) - maintains original behavior for 'file'
    if (depth === 0 && item.type === 'file') {
      return null
    }

    // For other types or depths:
    // Use the specific icon from iconMap if available
    const specificIcon = iconMap[item.type]
    if (specificIcon) {
      return specificIcon
    }

    // Fallback logic if no specific icon is found in iconMap for item.type
    // Check if it's a folder type or behaves like one (has children)
    if (item.type === 'folder' || (item.children && item.children.length > 0)) {
      return iconMap.folder || defaultIconMap.folder
    }

    // Default for other types (assumed file-like if not folder and no specific icon)
    return iconMap.file || defaultIconMap.file
  }

  const getItemPath = (targetItem: TreeViewItem, items: TreeViewItem[]): string => {
    const path: string[] = [targetItem.name]

    const findParent = (currentNode: TreeViewItem, currentScopeItems: TreeViewItem[]) => {
      for (let i = 0; i < currentScopeItems.length; i++) {
        const potentialParent = currentScopeItems[i]
        if (potentialParent.children?.some((child: TreeViewItem) => child.id === currentNode.id)) {
          path.unshift(potentialParent.name)
          findParent(potentialParent, items)
          break
        }
        if (potentialParent.children) {
          findParent(currentNode, potentialParent.children)
        }
      }
    }

    findParent(targetItem, items)
    return path.join(' → ')
  }

  // Add function to count selected items in a folder
  const getSelectedChildrenCount = useCallback(
    (targetItem: TreeViewItem): number => {
      let count = 0

      if (!targetItem.children) return 0

      // Inner recursive helper to avoid useCallback issues with direct recursion
      const countChildren = (currentItem: TreeViewItem): number => {
        let currentCount = 0
        if (!currentItem.children) return 0

        currentItem.children.forEach((child) => {
          if (selectedIds.has(child.id)) {
            currentCount += 1 // Fixed no-plusplus
          }
          if (child.children) {
            currentCount += countChildren(child) // Use the inner helper for recursion
          }
        })
        return currentCount
      }

      count = countChildren(targetItem)
      return count
    },
    [selectedIds],
  ) // Dependency for useCallback

  // "N selected" badge on TOP-LEVEL collapsed collections only. Per-article nested selection is NOT
  // offered: the picker selects a WHOLE space by its collection id (which carries every page under
  // it), and nested checkboxes are gated off in use-document-tree via `disableCheckbox: depth > 0`.
  // Enabling per-article selection needs the selection model reworked (nested rows into app_data +
  // parent/child check propagation), so it's intentionally out of scope here.
  const selectedCount = useMemo(() => {
    if (!item.children || !showAccessRights || depth > 0) return null
    return getSelectedChildrenCount(item)
  }, [item, showAccessRights, depth, getSelectedChildrenCount])

  const checkboxNode =
    // Checkboxes render on TOP-LEVEL rows only (`depth <= 0`). Nested rows are gated off via
    // `disableCheckbox: depth > 0` in use-document-tree; selecting a whole top-level space carries
    // every page under it, so per-article nested selection is intentionally not offered here.
    showAccessRights && depth <= 0 && !item.disableCheckbox ? (
      <div
        className={`relative flex items-center justify-center w-4 h-4 ${item.disableCheckbox ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
        role="checkbox"
        aria-checked={
          getCheckState(item, itemMap) === 'indeterminate'
            ? 'mixed'
            : getCheckState(item, itemMap) === 'checked'
        }
        tabIndex={!item.disableCheckbox ? 0 : -1}
        onClick={!item.disableCheckbox ? handleAccessClick : undefined}
        onKeyDown={
          !item.disableCheckbox
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault() // Prevent scrolling on spacebar
                  handleAccessClick(e as unknown as React.MouseEvent) // Cast properly to satisfy MouseEvent type
                }
              }
            : undefined
        }
      >
        {getCheckState(item, itemMap) === 'checked' && (
          <div className="w-4 h-4 border rounded bg-primary border-primary flex items-center justify-center">
            <svg
              className="h-3 w-3 text-primary-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        )}
        {getCheckState(item, itemMap) === 'unchecked' && (
          <div className="w-4 h-4 border rounded border-input" />
        )}
        {getCheckState(item, itemMap) === 'indeterminate' && (
          <div className="w-4 h-4 border rounded bg-primary border-primary flex items-center justify-center">
            <div className="h-0.5 w-2 bg-primary-foreground" />
          </div>
        )}
      </div>
    ) : null

  return (
    <ContextMenu>
      <ContextMenuTrigger>
        <div>
          <div
            ref={itemRef}
            data-tree-item
            data-id={item.id}
            data-depth={depth}
            data-folder-closed={item.children && !isOpen}
            className={`select-none cursor-pointer ${isSelected ? `${selectionStyle}` : 'text-foreground'} px-1`}
            style={{
              paddingLeft: `${depth === 0 ? 0 : (showAccessRights ? 48 : 32) + (depth - 1) * 24}px`,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClick(e as unknown as React.MouseEvent)
              }
            }}
            role="treeitem"
            aria-selected={isSelected}
            tabIndex={0}
          >
            <div className="flex items-center h-8">
              {item.children ? (
                <div className="flex items-center gap-1 flex-1 group">
                  {checkboxPosition === 'left' && checkboxNode}
                  <Collapsible open={isOpen} onOpenChange={(open) => onToggleExpand(item.id, open)}>
                    <CollapsibleTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <m.div
                          initial={false}
                          animate={{ rotate: isOpen ? 90 : 0 }}
                          transition={{ duration: 0.1 }}
                        >
                          <ChevronRightIcon className="h-4 w-4" />
                        </m.div>
                      </Button>
                    </CollapsibleTrigger>
                  </Collapsible>
                  {checkboxPosition !== 'left' && checkboxNode}
                  {renderIcon()} {/* This is for folder icon, currently null for collections */}
                  <span className="flex-1 text-s-500 truncate">{item.name}</span>
                  {/* Preview button for folders is not typical, so not adding it here */}
                  {showSelectionBadges && selectedCount !== null && selectedCount > 0 && (
                    <Badge variant="secondary" className="mr-2 bg-bg3 text-foreground hover:bg-hv1">
                      {selectedCount} selected
                    </Badge>
                  )}
                  {showItemDetailsOnHover && (
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 group-hover:opacity-100 opacity-0 items-center justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80">
                        <div className="space-y-2">
                          <h4 className="text-s-600">{item.name}</h4>
                          <div className="text-xs-400 text-muted-foreground space-y-1">
                            <div>
                              <span className="text-xs-500">Type:</span>{' '}
                              {item.type.charAt(0).toUpperCase() +
                                item.type.slice(1).replace('_', ' ')}
                            </div>
                            <div>
                              <span className="text-xs-500">ID:</span>{' '}
                              <span className="font-jetbrains tabular-nums">{item.id}</span>
                            </div>
                            <div>
                              <span className="text-xs-500">Location:</span>{' '}
                              {getItemPath(item, allItems)}
                            </div>
                            <div>
                              <span className="text-xs-500">Items:</span>{' '}
                              {item.children?.length || 0} direct items
                            </div>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  )}
                </div>
              ) : (
                <div
                  className={`flex items-center gap-1 flex-1 group relative ${(item.type === 'article' || item.type === 'file') && isHovered ? 'bg-hv1 rounded-md' : ''}`}
                >
                  {checkboxPosition === 'left' && checkboxNode}
                  <div className="h-6 w-6 flex items-center justify-center relative z-10">
                    {renderIcon()}
                  </div>
                  {checkboxPosition !== 'left' && checkboxNode}
                  <span className="flex-1 text-s-500 truncate relative z-10">{item.name}</span>
                  {isHovered &&
                    item.type !== 'folder' &&
                    onAction &&
                    menuItems?.find((mi) => mi.id === 'preview') && (
                      <Button
                        variant="ghost"
                        type="button"
                        size="sm"
                        className="opacity-100 items-center justify-center mr-1 px-2 h-6 relative z-10"
                        onClick={(e) => {
                          e.stopPropagation()
                          onAction('preview', [item])
                        }}
                        title="Preview"
                      >
                        Preview
                      </Button>
                    )}
                  {showItemDetailsOnHover && (
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 group-hover:opacity-100 opacity-0 items-center justify-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80">
                        <div className="space-y-2">
                          <h4 className="text-s-600">{item.name}</h4>
                          <div className="text-xs-400 text-muted-foreground space-y-1">
                            <div>
                              <span className="text-xs-500">Type:</span>{' '}
                              {item.type.charAt(0).toUpperCase() +
                                item.type.slice(1).replace('_', ' ')}
                            </div>
                            <div>
                              <span className="text-xs-500">ID:</span>{' '}
                              <span className="font-jetbrains tabular-nums">{item.id}</span>
                            </div>
                            <div>
                              <span className="text-xs-500">Location:</span>{' '}
                              {getItemPath(item, allItems)}
                            </div>
                          </div>
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  )}
                </div>
              )}
            </div>
          </div>

          {item.children && (
            <Collapsible open={isOpen} onOpenChange={(open) => onToggleExpand(item.id, open)}>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <CollapsibleContent forceMount asChild>
                    <m.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.05 }}
                    >
                      {item.children?.map((child) => (
                        <TreeItem
                          key={child.id}
                          item={child}
                          depth={depth + 1}
                          selectedIds={selectedIds}
                          lastSelectedId={lastSelectedId}
                          onSelect={onSelect}
                          expandedIds={expandedIds}
                          onToggleExpand={onToggleExpand}
                          getIcon={getIcon}
                          onAction={onAction}
                          onAccessChange={onAccessChange}
                          allItems={allItems}
                          showAccessRights={showAccessRights}
                          checkboxPosition={checkboxPosition}
                          itemMap={itemMap}
                          iconMap={iconMap}
                          menuItems={menuItems}
                          getSelectedItems={getSelectedItems}
                          showItemDetailsOnHover={showItemDetailsOnHover}
                          showSelectionBadges={showSelectionBadges}
                        />
                      ))}
                      {/* Lazy picker: collection's pages are being fetched on expand */}
                      {item.isLoading && (!item.children || item.children.length === 0) && (
                        <div
                          className="flex items-center gap-2 h-8 text-s-400 text-muted-foreground"
                          style={{
                            paddingLeft: `${(showAccessRights ? 48 : 32) + depth * 24}px`,
                          }}
                        >
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Loading pages…
                        </div>
                      )}
                    </m.div>
                  </CollapsibleContent>
                )}
              </AnimatePresence>
            </Collapsible>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-64">
        {menuItems?.map((menuItem) => (
          <ContextMenuItem
            key={menuItem.id}
            onClick={() => {
              const items = selectedIds.has(item.id) ? getSelectedItems() : [item]
              menuItem.action(items)
            }}
          >
            {menuItem.icon && <span className="mr-2 h-4 w-4">{menuItem.icon}</span>}
            {menuItem.label}
          </ContextMenuItem>
        ))}
      </ContextMenuContent>
    </ContextMenu>
  )
}

const TreeView = ({
  className,
  checkboxLabels = {
    check: 'Check',
    uncheck: 'Uncheck',
  },
  data,
  iconMap,
  searchPlaceholder = 'Search...',
  selectionText = 'selected',
  showExpandAll = true,
  showCheckboxes = false,
  showSearchBox = true, // Default to true to maintain existing behavior
  showItemDetailsOnHover = true,
  showSelectionHeader = true,
  checkboxPosition = 'left',
  getIcon,
  onSelectionChange,
  onAction,
  onCheckChange,
  menuItems,
  showSelectionBadges = true,
  defaultCheckedIds,
  onNewSelectionChange,
  onExpand,
}: TreeViewProps) => {
  const [currentMousePos, setCurrentMousePos] = useState<number>(0)
  const [dragStart, setDragStart] = useState<number | null>(null)
  const [dragStartPosition, setDragStartPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [isDragging, setIsDragging] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')

  const dragRef = useRef<HTMLDivElement>(null)
  const lastSelectedId = useRef<string | null>(null)
  const treeRef = useRef<HTMLDivElement>(null)

  const DRAG_THRESHOLD = 10 // pixels

  // Apply default checked state to data
  const dataWithDefaults = useMemo(() => {
    if (!defaultCheckedIds || defaultCheckedIds.length === 0) return data

    const updateItemsWithDefaultChecked = (items: TreeViewItem[]): TreeViewItem[] =>
      items.map((item) => ({
        ...item,
        defaultChecked: defaultCheckedIds.includes(item.id),
        children: item.children ? updateItemsWithDefaultChecked(item.children) : undefined,
      }))

    return updateItemsWithDefaultChecked(data)
  }, [data, defaultCheckedIds])

  // Create a map of all items by ID
  const itemMap = useMemo(() => buildItemMap(dataWithDefaults), [dataWithDefaults])

  // Memoize the search results and expanded IDs
  const { filteredData, searchExpandedIds } = useMemo(() => {
    if (!searchQuery.trim()) {
      return { filteredData: dataWithDefaults, searchExpandedIds: new Set<string>() }
    }

    const searchLower = searchQuery.toLowerCase()
    const newExpandedIds = new Set<string>()

    // Helper function to check if an item or its descendants match the search
    const itemMatches = (item: TreeViewItem): boolean => {
      const nameMatches = item.name.toLowerCase().includes(searchLower)
      if (nameMatches) return true

      if (item.children) {
        return item.children.some((child) => itemMatches(child))
      }

      return false
    }

    // Helper function to filter tree while keeping parent structure
    const filterTree = (items: TreeViewItem[]): TreeViewItem[] =>
      items
        .map((item) => {
          if (!item.children) {
            return itemMatches(item) ? item : null
          }

          const filteredChildren = filterTree(item.children)
          if (filteredChildren.length > 0 || itemMatches(item)) {
            if (item.children) {
              newExpandedIds.add(item.id)
            }
            return {
              ...item,
              children: filteredChildren,
            }
          }
          return null
        })
        .filter((item): item is TreeViewItem => item !== null)

    return {
      filteredData: filterTree(dataWithDefaults),
      searchExpandedIds: newExpandedIds,
    }
  }, [dataWithDefaults, searchQuery])

  // Update expanded IDs when search changes
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedIds((prev) => new Set([...prev, ...searchExpandedIds]))
    }
  }, [searchExpandedIds, searchQuery])

  useEffect(() => {
    const handleClickAway = (e: MouseEvent) => {
      const target = e.target as Element

      const clickedInside =
        treeRef.current?.contains(target) ||
        dragRef.current?.contains(target) ||
        // Ignore clicks on context menus
        target.closest('[role="menu"]') ||
        target.closest('[data-radix-popper-content-wrapper]')

      if (!clickedInside) {
        setSelectedIds(new Set())
        lastSelectedId.current = null
      }
    }

    document.addEventListener('mousedown', handleClickAway)
    return () => document.removeEventListener('mousedown', handleClickAway)
  }, [])

  // Function to collect all folder IDs
  const getAllFolderIds = (items: TreeViewItem[]): string[] => {
    let ids: string[] = []
    items.forEach((item) => {
      if (item.children) {
        ids.push(item.id)
        ids = [...ids, ...getAllFolderIds(item.children)]
      }
    })
    return ids
  }

  const handleExpandAll = () => {
    setExpandedIds(new Set(getAllFolderIds(dataWithDefaults)))
  }

  const handleCollapseAll = () => {
    setExpandedIds(new Set())
  }

  const handleToggleExpand = (id: string, isOpen: boolean) => {
    const newExpandedIds = new Set(expandedIds)
    if (isOpen) {
      newExpandedIds.add(id)
      // Lazy picker: tell the parent this node opened so it can fetch the collection's children on
      // demand (the parent dedups, so re-opening an already-loaded node is a no-op fetch).
      const item = itemMap.get(id)
      if (item) onExpand?.(item)
    } else {
      newExpandedIds.delete(id)
    }
    setExpandedIds(newExpandedIds)
  }

  // Get selected items
  const getSelectedItems = useCallback((): TreeViewItem[] => {
    const items: TreeViewItem[] = []
    const processItem = (item: TreeViewItem) => {
      if (selectedIds.has(item.id)) {
        items.push(item)
      }
      item.children?.forEach(processItem)
    }
    dataWithDefaults.forEach(processItem)
    return items
  }, [selectedIds, dataWithDefaults])

  // Get only newly selected items (excluding default checked ones)
  const getNewlySelectedItems = useCallback((): TreeViewItem[] => {
    const items: TreeViewItem[] = []
    const processItem = (item: TreeViewItem) => {
      // Only include items that are checked but not default checked
      if (item.checked && !item.defaultChecked) {
        items.push(item)
      }
      item.children?.forEach(processItem)
    }
    dataWithDefaults.forEach(processItem)
    return items
  }, [dataWithDefaults])

  // Get selected items, filtering out parents if their children are selected
  const getEffectiveSelectedItems = useCallback((): TreeViewItem[] => {
    const selectedItems = getSelectedItems()

    // Build a set of all selected IDs for quick lookup
    const selectedIdsSet = new Set(selectedItems.map((item) => item.id))

    // Filter out parents whose children are also selected
    return selectedItems.filter((item) => {
      // If this item has no children, always include it
      if (!item.children) return true

      // Check if any children of this item are selected
      const hasSelectedChildren = item.children.some((child) => selectedIdsSet.has(child.id))

      // Only include this item if none of its children are selected
      return !hasSelectedChildren
    })
  }, [getSelectedItems])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only track on left click and not on buttons
    if (e.button !== 0 || (e.target as HTMLElement).closest('button')) return

    setDragStartPosition({ x: e.clientX, y: e.clientY })
  }, [])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Check if primary button is still held down
      if (!(e.buttons & 1)) {
        setIsDragging(false)
        setDragStart(null)
        setDragStartPosition(null)
        return
      }

      // If we haven't registered a potential drag start position, ignore
      if (!dragStartPosition) return

      // Calculate distance moved
      const deltaX = e.clientX - dragStartPosition.x
      const deltaY = e.clientY - dragStartPosition.y
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

      // If we haven't started dragging yet, check if we should start
      if (!isDragging) {
        if (distance > DRAG_THRESHOLD) {
          setIsDragging(true)
          setDragStart(dragStartPosition.y)

          // Clear selection if not holding shift/ctrl
          if (!e.shiftKey && !e.ctrlKey) {
            setSelectedIds(new Set())
            lastSelectedId.current = null
          }
        }
        return
      }

      // Rest of the existing drag logic
      if (!dragRef.current) return

      const items = Array.from(
        dragRef.current.querySelectorAll('[data-tree-item]'),
      ) as HTMLElement[]

      const startY = dragStart
      const currentY = e.clientY
      const [selectionStart, selectionEnd] = [
        Math.min(startY || 0, currentY),
        Math.max(startY || 0, currentY),
      ]

      const newSelection = new Set(e.shiftKey || e.ctrlKey ? Array.from(selectedIds) : [])

      items.forEach((item) => {
        const rect = item.getBoundingClientRect()
        const itemTop = rect.top
        const itemBottom = rect.top + rect.height

        if (itemBottom >= selectionStart && itemTop <= selectionEnd) {
          const id = item.getAttribute('data-id')
          const isClosedFolder = item.getAttribute('data-folder-closed') === 'true'
          const parentFolderClosed = item.closest('[data-folder-closed="true"]')

          if (id && (isClosedFolder || !parentFolderClosed)) {
            newSelection.add(id)
          }
        }
      })

      setSelectedIds(newSelection)
      setCurrentMousePos(e.clientY)
    },
    [isDragging, dragStart, selectedIds, dragStartPosition],
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setDragStart(null)
    setDragStartPosition(null)
  }, [])

  // Add cleanup for mouse events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('mouseleave', handleMouseUp)
    }
    return () => {
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('mouseleave', handleMouseUp)
    }
  }, [isDragging, handleMouseUp])

  // Call onSelectionChange when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(getSelectedItems())
    }
  }, [selectedIds, onSelectionChange, getSelectedItems])

  // Call onNewSelectionChange when data changes (for newly selected items only)
  useEffect(() => {
    if (onNewSelectionChange) {
      onNewSelectionChange(getNewlySelectedItems())
    }
  }, [dataWithDefaults, onNewSelectionChange, getNewlySelectedItems])

  return (
    // Single LazyMotion boundary for the whole tree — every `m.*` below (here and in the recursively
    // rendered TreeItem) resolves its features from this provider.
    <LazyMotion features={domAnimation}>
      <div className="flex gap-4">
        <div ref={treeRef} className="bg-background px-6 rounded-xl space-y-4 w-full">
          <AnimatePresence mode="wait">
            {showSelectionHeader && selectedIds.size > 0 ? (
              <m.div
                key="selection"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="h-10 flex items-center justify-between bg-background rounded-lg border px-4"
              >
                <Button
                  type="button"
                  className="cursor-pointer flex items-center bg-transparent border-none p-0"
                  variant="ghost"
                  size="sm"
                  title="Clear selection"
                  onClick={() => {
                    setSelectedIds(new Set())
                    lastSelectedId.current = null
                  }}
                >
                  <Cross2Icon className="h-4 w-4 mr-2" />
                  {selectedIds.size} {selectionText}
                </Button>

                {showCheckboxes && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const effectiveItems = getEffectiveSelectedItems()
                        const processItem = (item: TreeViewItem) => {
                          onCheckChange?.(item, true)
                          item.children?.forEach(processItem)
                        }
                        effectiveItems.forEach(processItem)
                      }}
                      className="text-status-success hover:text-status-success hover:bg-status-success/10"
                    >
                      {checkboxLabels.check}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const effectiveItems = getEffectiveSelectedItems()
                        const processItem = (item: TreeViewItem) => {
                          onCheckChange?.(item, false)
                          item.children?.forEach(processItem)
                        }
                        effectiveItems.forEach(processItem)
                      }}
                      className="text-status-danger hover:text-status-danger hover:bg-status-danger/[0.08]"
                    >
                      {checkboxLabels.uncheck}
                    </Button>
                  </div>
                )}
              </m.div>
            ) : (
              (showSearchBox || showExpandAll) && (
                <m.div
                  key="search-or-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="h-10 flex items-center gap-2"
                >
                  {showSearchBox ? (
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder={searchPlaceholder}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-10 pl-9"
                      />
                    </div>
                  ) : (
                    <div className="flex-1" role="none" /> // Empty div to maintain layout if no search and no title
                  )}
                  {showExpandAll && (
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 px-2"
                        onClick={handleExpandAll}
                      >
                        <ChevronDownIcon className="h-4 w-4 mr-1" />
                        Expand All
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 px-2"
                        onClick={handleCollapseAll}
                      >
                        <ChevronRightIcon className="h-4 w-4 mr-1" />
                        Collapse All
                      </Button>
                    </div>
                  )}
                </m.div>
              )
            )}
          </AnimatePresence>

          <div
            ref={dragRef}
            className={cn('rounded-lg bg-card relative select-none', className)}
            role="group"
            aria-label="Tree view with drag selection"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
          >
            {isDragging && (
              <div
                className="absolute inset-0 bg-transparent pointer-events-none"
                style={{
                  top: Math.min(dragStart || 0, dragStart === null ? 0 : currentMousePos),
                  height: Math.abs((dragStart || 0) - (dragStart === null ? 0 : currentMousePos)),
                }}
              />
            )}
            {filteredData.map((item) => (
              <TreeItem
                key={item.id}
                item={item}
                selectedIds={selectedIds}
                lastSelectedId={lastSelectedId}
                onSelect={setSelectedIds}
                expandedIds={expandedIds}
                onToggleExpand={handleToggleExpand}
                getIcon={getIcon}
                onAction={onAction}
                onAccessChange={onCheckChange}
                allItems={dataWithDefaults}
                showAccessRights={showCheckboxes}
                checkboxPosition={checkboxPosition}
                itemMap={itemMap}
                iconMap={iconMap}
                menuItems={menuItems}
                getSelectedItems={getSelectedItems}
                showItemDetailsOnHover={showItemDetailsOnHover}
                showSelectionBadges={showSelectionBadges}
              />
            ))}
          </div>
        </div>
      </div>
    </LazyMotion>
  )
}
export default TreeView
